import { ArrowRight, Home, Mail } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';

export function NotFound(): JSX.Element {
  useEffect(() => {
    const previous = document.title;
    document.title = 'Page not found | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <>
      <PageHero
        eyebrow="404"
        title="We can't find that page"
        lead="The page you tried to reach has moved or never existed. Head back to the home page, or follow one of the links below to find what you were looking for."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mx-auto grid max-w-3xl gap-4 sm:grid-cols-2">
            <Link
              to="/"
              className="flex items-start gap-4 rounded-lg border border-brand-surface bg-brand-white p-6 shadow-card transition-shadow hover:shadow-card-hover"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                <Home size={20} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow-deep">
                  Home
                </p>
                <p className="mt-1 text-base font-bold text-brand-charcoal">Sunset Harbor home</p>
                <p className="mt-2 text-sm text-brand-body-medium">
                  Building overview, amenities, and quick links.
                </p>
              </div>
            </Link>

            <Link
              to="/documents"
              className="flex items-start gap-4 rounded-lg border border-brand-surface bg-brand-white p-6 shadow-card transition-shadow hover:shadow-card-hover"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                <ArrowRight size={20} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow-deep">
                  Documents
                </p>
                <p className="mt-1 text-base font-bold text-brand-charcoal">Public records</p>
                <p className="mt-2 text-sm text-brand-body-medium">
                  Minutes, budget, declarations, and amendments.
                </p>
              </div>
            </Link>

            <Link
              to="/contact"
              className="flex items-start gap-4 rounded-lg border border-brand-surface bg-brand-white p-6 shadow-card transition-shadow hover:shadow-card-hover"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                <Mail size={20} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow-deep">
                  Contact
                </p>
                <p className="mt-1 text-base font-bold text-brand-charcoal">Reach the board</p>
                <p className="mt-2 text-sm text-brand-body-medium">
                  Records requests, real-estate inquiries, anything else.
                </p>
              </div>
            </Link>

            <Link
              to="/login"
              className="flex items-start gap-4 rounded-lg border border-brand-charcoal bg-brand-charcoal p-6 text-brand-white shadow-card transition-shadow hover:shadow-card-hover"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal">
                <ArrowRight size={20} strokeWidth={1.75} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-yellow">
                  Owners
                </p>
                <p className="mt-1 text-base font-bold">Owner portal</p>
                <p className="mt-2 text-sm text-brand-white/80">
                  Sign in for owner-only records and the records-request form.
                </p>
              </div>
            </Link>
          </div>
        </Container>
      </section>
    </>
  );
}
